import Feature from "ol/Feature.js";
import Point from "ol/geom/Point.js";
import VectorLayer from "ol/layer/Vector.js";
import VectorSource from "ol/source/Vector.js";
import { fromLonLat } from "ol/proj.js";
import { getValidSensorPointCoordinate } from "../sensorGeometry";
import { enrichSensorFeatures } from "../sensors/sensorStatus";
import { sensorStyleFunction } from "../sensors/sensorStyle";

export const FIBSEN_SENSORS_LAYER_ID = "fibsen-sensors";
export const FIBSEN_SENSORS_Z_INDEX = 30;
export const SENSOR_DATA_PROJECTION = "EPSG:4326";
export const SENSOR_FEATURE_PROJECTION = "EPSG:3857";

const sensorCode = (sensor) =>
  String(sensor?.code || sensor?.externalId || sensor?.deviceId || "").trim();

const sensorMetadata = (sensor) =>
  sensor?.metadata && typeof sensor.metadata === "object"
    ? sensor.metadata
    : {};

const selectedSensorIdSet = (selectedSensorIds) =>
  new Set(
    Array.isArray(selectedSensorIds)
      ? selectedSensorIds
      : selectedSensorIds
        ? [selectedSensorIds]
        : []
  );

export const createFibsenSensorFeatures = (
  apiSensors = [],
  selectedSensorIds = [],
  now = Date.now()
) => {
  const selectedIds = selectedSensorIdSet(selectedSensorIds);
  const features = (Array.isArray(apiSensors) ? apiSensors : [])
    .map((sensor) => {
      const coordinates = getValidSensorPointCoordinate(sensor);
      if (!coordinates) return null;

      const metadata = sensorMetadata(sensor);
      const feature = new Feature({
        geometry: new Point(
          fromLonLat(coordinates, SENSOR_FEATURE_PROJECTION)
        ),
        sensorId: sensor.id,
        code: sensorCode(sensor),
        source: sensor.source || metadata.source || null,
        reference_origin:
          sensor.reference_origin || metadata.reference_origin || null,
        geometry4326: [...coordinates],
        apiSensor: sensor,
        selected: selectedIds.has(sensor.id),
      });
      if (sensor.id) feature.setId(sensor.id);
      return feature;
    })
    .filter(Boolean);

  return enrichSensorFeatures(features, now);
};

export const createFibsenSensorLayer = ({
  apiSensors = [],
  selectedSensorIds = [],
  now = Date.now(),
} = {}) => {
  const source = new VectorSource({
    features: createFibsenSensorFeatures(
      apiSensors,
      selectedSensorIds,
      now
    ),
  });
  const layer = new VectorLayer({
    source,
    style: sensorStyleFunction,
    visible: true,
    zIndex: FIBSEN_SENSORS_Z_INDEX,
  });
  layer.setProperties({
    id: FIBSEN_SENSORS_LAYER_ID,
    title: "HortaTech sensors",
  });
  return layer;
};

export const updateFibsenSensorFeatures = (
  layer,
  apiSensors = [],
  selectedSensorIds = [],
  now = Date.now()
) => {
  const source = layer?.getSource?.();
  if (!source) return;

  source.clear(true);
  source.addFeatures(
    createFibsenSensorFeatures(apiSensors, selectedSensorIds, now)
  );
  layer?.changed?.();
};

export const updateFibsenSensorSelection = (layer, selectedSensorIds) => {
  const selectedIds = selectedSensorIdSet(selectedSensorIds);
  const features = layer?.getSource()?.getFeatures?.() || [];
  features.forEach((feature) => {
    feature.set("selected", selectedIds.has(feature.get("sensorId")));
  });
  layer?.changed?.();
};

export const findFibsenSensorFeature = (layer, sensorId) => {
  if (!layer || !sensorId) return null;
  const source = layer.getSource?.();
  const byFeatureId = source?.getFeatureById?.(sensorId);
  if (byFeatureId) return byFeatureId;

  return (
    source?.getFeatures?.().find(
      (feature) => feature.get("sensorId") === sensorId
    ) || null
  );
};

export const sensorSelectionFromFeature = (feature) => {
  const properties = feature?.getProperties?.() || {};
  const coordinates = properties.geometry4326;
  const apiSensor = properties.apiSensor;
  if (!apiSensor || !getValidSensorPointCoordinate({
    geometry: { type: "Point", coordinates },
  })) {
    return null;
  }

  return {
    ...apiSensor,
    code: properties.code,
    source: properties.source,
    reference_origin: properties.reference_origin,
    status: properties.status === "online" ? "online" : "offline",
    lastMeasurementAt: properties.lastMeasurementAt || null,
    geometry: { type: "Point", coordinates: [...coordinates] },
    apiSensor,
  };
};
